// apps/client/src/pages/dashboard/Dashboard.tsx
import {
  AssignmentTwoTone,
  CloudSyncTwoTone,
  PlaylistAddTwoTone,
  ScienceTwoTone,
} from '@mui/icons-material';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import PaperSection from '../../components/PaperSection';
import { useAuth } from '../../contexts/AuthContext';
import ContentWrapper from '../../layout/ContentWrapper';
import OnboardingCard from './components/OnboardingCard';
import { useProject } from '../../contexts/ProjectContext';
import ProjectSummaryCard from './components/ProjectSummaryCard';
import AddProjectCard from './components/AddProjectCard';
import { apiClient } from '../../services';
import { Project, ProjectListResponse, UserRole } from '@avanzu/types';
import { EmptyStateContent } from '../../components/EmptyStateContent';

export const DashboardPageRoles = [
  UserRole.AVANZU,
  UserRole.OWNER,
  UserRole.ADMIN,
  UserRole.MEMBER,
  UserRole.VIEWER,
];

export const Dashboard: React.FC = () => {
  const { user, currentOrganisation, organisationId } = useAuth();
  const { currentProject, currentProjectId, setCurrentProject } = useProject();
  const userName = user?.firstName || '';

  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  useEffect(() => {
    if (organisationId) {
      fetchProjects();
    }
  }, [organisationId]);

  const fetchProjects = async () => {
    if (!organisationId) return;

    try {
      setLoadingProjects(true);
      const response = await apiClient.get(`/organisations/${organisationId}/projects`);
      const data: ProjectListResponse = await response.json();

      if (data.success && data.projects) {
        setProjects(data.projects);
      }
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleProjectSelect = (projectId: string) => {
    setCurrentProject(projectId);
  };

  const onboardingSteps = [
    {
      stepNumber: 1,
      title: 'Connect to Xero',
      description: 'Link your Xero account to sync your financial data and transactions.',
      icon: <CloudSyncTwoTone sx={{ fontSize: 48 }} color="primary" />,
      completed: currentOrganisation?.connectedToXero || false,
      path: '/settings/xero',
    },
    {
      stepNumber: 2,
      title: 'Create your R&D Plan',
      description: 'Set up your core and supporting activities for the tax year.',
      icon: (
        <>
          <AssignmentTwoTone sx={{ fontSize: 36 }} color="primary" />
          <ScienceTwoTone sx={{ fontSize: 48 }} color="primary" />
        </>
      ),
      completed: currentOrganisation?.createdProject || false,
      path: `/app/projects`,
    },
    {
      stepNumber: 3,
      title: 'Map transactions',
      description: 'Allocate your transactions to R&D activities to track your rebate.',
      icon: <PlaylistAddTwoTone sx={{ fontSize: 48 }} color="primary" />,
      completed: currentOrganisation?.mappedFirstTransaction || false,
      path: `/app/transactions/${currentProject?.id || ''}`,
    },
  ];

  // Check if all onboarding steps are complete
  const allOnboardingComplete = onboardingSteps.every((step) => step.completed);

  return (
    <ContentWrapper>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ mb: 2, fontWeight: 600 }}>
          Welcome, {userName}
        </Typography>

        {!allOnboardingComplete && (
          <>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 500 }}>
              Get started
            </Typography>

            <Grid container spacing={3} sx={{ mb: 4 }}>
              {onboardingSteps.map((step) => (
                <Grid item xs={12} md={4} key={step.stepNumber}>
                  <OnboardingCard {...step} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>

      {/* Projects Overview Section */}
      <PaperSection title="Your R&D Projects">
        {loadingProjects ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : projects.length === 0 ? (
          <EmptyStateContent
            icon={<AssignmentTwoTone />}
            title="No projects yet"
            message="Create your first R&D project to get started tracking your research and development activities."
            button={
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Visit the Projects page to create a new project
                </Typography>
              </Box>
            }
            showBorder={false}
          />
        ) : (
          <Box>
            <Grid container spacing={3}>
              {/* Add Project Card - always first */}
              <Grid item xs={12} md={6} lg={4}>
                <AddProjectCard />
              </Grid>

              {/* Existing Projects */}
              {projects
                .sort((a, b) => {
                  // Sort by tax year descending, then by name
                  const yearCompare = b.taxYear.localeCompare(a.taxYear);
                  if (yearCompare !== 0) return yearCompare;
                  return a.name.localeCompare(b.name);
                })
                .map((project) => (
                  <Grid item xs={12} md={6} lg={4} key={project.id}>
                    <ProjectSummaryCard
                      project={project}
                      organisationId={organisationId!}
                      isSelected={currentProjectId === project.id}
                      onSelect={handleProjectSelect}
                    />
                  </Grid>
                ))}
            </Grid>
          </Box>
        )}
      </PaperSection>
    </ContentWrapper>
  );
};

export default Dashboard;
